import { useEffect, useCallback } from 'react';
import { useDataApi } from '@adsk/alloy-helpers';
import { TRACK_EVENTS } from '@common/constants';
import { ACTIONS } from '../state/actions';
import {
  LICENSE_OPERATION_TYPE,
  LICENSE_RESPONSE_STATUSES,
  USER_ACCOUNT_OFFERING_TYPES,
  TRACK_EVENTS_STATUS,
} from '../constants';
import { buildUnassignOfferingRequest } from '../utils';

const useUnassignOffering = ({
  accountId,
  assignDropdownLicense,
  dispatch,
  licenseDropdownPreviousValue,
  onUpdateOffering,
  user,
  userOfferingsData,
  sendAnalytics,
  setUserOfferings,
  state,
}) => {
  const {
    data: unassignOfferingData,
    error: unassignOfferingError,
    doFetch: doUnassignOfferingFetch,
    loading: loadingUnassignOfferingData,
  } = useDataApi();

  const handleUnassignError = (
    type,
    payload,
    category,
    categoryName,
    licensingModel
  ) => {
    dispatch({ type, payload });
    dispatch({
      type: ACTIONS.SET_LICENSE_OPERATION,
      payload: {
        category,
        categoryName,
        licensingModel,
        type: LICENSE_OPERATION_TYPE.UNASSIGN,
        saved: false,
        error: true,
        assignAfterOperation: false,
      },
    });
  };

  const unassignDropdownLicense = useCallback(
    (
      plc,
      externalKey,
      assignAfterOperation,
      category,
      categoryName,
      licensingModel
    ) => {
      doUnassignOfferingFetch(
        ...buildUnassignOfferingRequest(user.id, accountId, externalKey)
      );
      dispatch({
        type: ACTIONS.SET_LICENSE_OPERATION,
        payload: {
          category,
          categoryName,
          plc,
          licensingModel,
          type: LICENSE_OPERATION_TYPE.UNASSIGN,
          saved: false,
          error: false,
          assignAfterOperation,
        },
      });
    },
    [doUnassignOfferingFetch, user, accountId, dispatch]
  );

  useEffect(() => {
    if (unassignOfferingData === null) return;

    const {
      plc,
      category,
      categoryName,
      assignAfterOperation,
      licensingModel,
    } = state.subscription.currentOperation;
    const { selectedOffering } = state.subscription.currentOfferings[category];

    if (assignAfterOperation) {
      assignDropdownLicense(
        selectedOffering.plc,
        selectedOffering.offeringExternalKey,
        category,
        categoryName,
        selectedOffering.licensingModel,
        true
      );
    } else {
      sendAnalytics(
        TRACK_EVENTS.NAME.SUBSCRIPTION_UNASSIGN,
        TRACK_EVENTS_STATUS.SUCCESS,
        categoryName,
        selectedOffering
      );
      dispatch({
        type: ACTIONS.SET_LICENSE_OPERATION,
        payload: {
          category,
          categoryName,
          licensingModel,
          type: LICENSE_OPERATION_TYPE.UNASSIGN,
          saved: true,
          error: false,
          assignAfterOperation: false,
        },
      });
    }
    setUserOfferings({
      results: userOfferingsData.results.map((offering) => {
        if (
          offering.plc === plc &&
          offering.licensingModel === licensingModel
        ) {
          return { ...offering, type: USER_ACCOUNT_OFFERING_TYPES.NONE };
        }
        return offering;
      }),
    });
    onUpdateOffering(
      LICENSE_RESPONSE_STATUSES.DELETED,
      LICENSE_OPERATION_TYPE.UNASSIGN,
      {
        plc,
        categoryName,
        assignAfterOperation,
        licensingModel,
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassignOfferingData]);

  useEffect(() => {
    if (unassignOfferingError === null) return;

    const {
      plc,
      category,
      categoryName,
      assignAfterOperation,
      licensingModel,
    } = state.subscription.currentOperation;
    const { selectedOffering } = state.subscription.currentOfferings[category];

    sendAnalytics(
      assignAfterOperation
        ? TRACK_EVENTS.NAME.SUBSCRIPTION_CHANGE
        : TRACK_EVENTS.NAME.SUBSCRIPTION_UNASSIGN,
      TRACK_EVENTS_STATUS.FAILURE,
      categoryName,
      selectedOffering
    );
    handleUnassignError(
      ACTIONS.SET_SELECTED_OFFERING,
      {
        category,
        offering: licenseDropdownPreviousValue.current,
      },
      category,
      categoryName,
      licensingModel
    );
    onUpdateOffering(
      unassignOfferingError.status,
      LICENSE_OPERATION_TYPE.UNASSIGN,
      {
        plc,
        categoryName,
        assignAfterOperation,
        licensingModel,
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassignOfferingError]);

  return { unassignDropdownLicense, loadingUnassignOfferingData };
};

export default useUnassignOffering;
